import { usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useState } from "react";

export type PurchaseRequestShowViewMode = "full" | "simple";

const VIEW_QUERY_PARAM = "view";
const VIEW_STORAGE_KEY = "purchase_requests.show.view";

const parseViewMode = (
  value: string | null | undefined,
): PurchaseRequestShowViewMode | null => {
  if (value === "simple" || value === "full") return value;
  return null;
};

const parseSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const hasViewParam = params.has(VIEW_QUERY_PARAM);
  const rawView = params.get(VIEW_QUERY_PARAM);
  const parsedView = parseViewMode(rawView);
  const hasInvalidView = hasViewParam && !parsedView;

  return {
    hasViewParam,
    parsedView,
    hasInvalidView,
  };
};

const replaceUrlViewParam = (mode: PurchaseRequestShowViewMode) => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.searchParams.set(VIEW_QUERY_PARAM, mode);
  window.history.replaceState(window.history.state, "", url.toString());
};

const resolveMode = (
  search: string,
  storedMode: PurchaseRequestShowViewMode | null,
) => {
  const { parsedView, hasViewParam, hasInvalidView } = parseSearch(search);

  if (hasInvalidView) return "full" as const;
  if (hasViewParam && parsedView) return parsedView;
  if (storedMode) return storedMode;
  return "full" as const;
};

export function usePurchaseRequestViewMode() {
  const page = usePage();

  const pageSearch = useMemo(() => {
    const url = typeof page.url === "string" ? page.url : "";
    const queryIndex = url.indexOf("?");
    if (queryIndex === -1) return "";
    return url.slice(queryIndex + 1);
  }, [page.url]);

  const initialMode = useMemo(() => {
    const { parsedView, hasInvalidView } = parseSearch(pageSearch);
    if (hasInvalidView) return "full";
    return parsedView ?? "full";
  }, [pageSearch]);

  const [viewMode, setViewModeState] = useState<PurchaseRequestShowViewMode>(
    () => {
      if (typeof window === "undefined") return initialMode;
      const storedMode = parseViewMode(
        window.localStorage.getItem(VIEW_STORAGE_KEY),
      );
      return resolveMode(window.location.search, storedMode);
    },
  );

  // Keep state in sync with URL/localStorage on each page navigation.
  useEffect(() => {
    if (typeof window === "undefined") {
      setViewModeState(initialMode);
      return;
    }

    const { hasInvalidView } = parseSearch(window.location.search);
    const storedMode = parseViewMode(
      window.localStorage.getItem(VIEW_STORAGE_KEY),
    );
    const nextMode = resolveMode(window.location.search, storedMode);

    if (hasInvalidView) {
      replaceUrlViewParam("full");
    }

    window.localStorage.setItem(VIEW_STORAGE_KEY, nextMode);
    setViewModeState(nextMode);
  }, [initialMode, page.url]);

  const setViewMode = useCallback((mode: PurchaseRequestShowViewMode) => {
    setViewModeState(mode);

    if (typeof window === "undefined") return;

    window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
    replaceUrlViewParam(mode);
  }, []);

  return {
    viewMode,
    setViewMode,
  } as const;
}
